/**
 * FeedLoop - Sistema de Reporte de Fallas y Sugerencias
 * Todos los usuarios pueden ver y crear reportes
 * Solo admin puede actualizar estados y generar prompts
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Bug, 
  Lightbulb, 
  Plus, 
  Copy, 
  Check,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Send,
  Eye,
  Pencil
} from 'lucide-react';
import { useAuthStore } from '../store';
import api from '../services/api';

const STATUS_CONFIG = {
  enviado: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Send },
  recibido: { label: 'Recibido', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Eye },
  en_proceso: { label: 'En Proceso', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  solucionado: { label: 'Solucionado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  sin_solucion: { label: 'Sin Solución', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', icon: XCircle },
  aceptado: { label: 'Aceptado', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  rechazado: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle }
};

const PRIORITY_CONFIG = {
  baja: { label: 'Baja', color: 'bg-gray-500/20 text-gray-400' },
  media: { label: 'Media', color: 'bg-blue-500/20 text-blue-400' },
  alta: { label: 'Alta', color: 'bg-orange-500/20 text-orange-400' },
  critica: { label: 'Crítica', color: 'bg-red-500/20 text-red-400' }
};

export default function FeedLoop() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  
  const [issues, setIssues] = useState([]);
  const [stats, setStats] = useState({});
  const [categories, setCategories] = useState({ falla: [], sugerencia: [] });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [myReports, setMyReports] = useState(false);
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'falla',
    title: '',
    description: '',
    category: '',
    priority: 'media',
    affected_module: '',
    steps_to_reproduce: '',
    expected_behavior: ''
  });
  
  const [editData, setEditData] = useState({
    status: '',
    admin_notes: '',
    resolution_notes: ''
  });
  
  const [copied, setCopied] = useState(false);
  const [expandedIssue, setExpandedIssue] = useState(null);

  useEffect(() => {
    loadData();
  }, [filterType, filterStatus, myReports]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load categories
      const catRes = await api.get('/issues/categories');
      setCategories(catRes.data);
      
      // Load stats
      const statsRes = await api.get('/issues/stats');
      setStats(statsRes.data);
      
      // Load issues with filters
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (myReports) params.append('my_reports', 'true');
      
      const issuesRes = await api.get(`/issues?${params.toString()}`);
      setIssues(issuesRes.data.issues || []);
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    
    try {
      const res = await api.post('/issues', formData);
      toast.success('Reporte creado exitosamente');
      setShowCreateModal(false);
      setFormData({
        type: 'falla',
        title: '',
        description: '',
        category: '',
        priority: 'media',
        affected_module: '',
        steps_to_reproduce: '',
        expected_behavior: ''
      });
      loadData();
    } catch (err) {
      console.error('Error creating issue:', err);
      toast.error('Error al crear reporte');
    }
  };

  const handleUpdate = async () => {
    if (!selectedIssue) return;
    
    try {
      await api.put(`/issues/${selectedIssue.id}`, editData);
      toast.success('Issue actualizado');
      setShowEditModal(false);
      loadData();
    } catch (err) {
      console.error('Error updating issue:', err);
      toast.error('Error al actualizar');
    }
  };

  const copyPrompt = async (issue) => {
    try {
      const res = await api.get(`/issues/${issue.id}/prompt`);
      await navigator.clipboard.writeText(res.data.prompt);
      setCopied(true);
      toast.success('Prompt copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying prompt:', err);
      toast.error('Error al copiar prompt');
    }
  };

  const openDetail = (issue) => {
    setSelectedIssue(issue);
    setShowDetailModal(true);
  };

  const openEdit = (issue) => {
    setSelectedIssue(issue);
    setEditData({
      status: issue.status || 'enviado',
      admin_notes: issue.admin_notes || '',
      resolution_notes: issue.resolution_notes || ''
    });
    setShowEditModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              FeedLoop
            </h1>
            <p className="text-muted-foreground mt-1">
              Fallas y Sugerencias
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Reporte
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <Bug className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.total_fallas || 0}</p>
                <p className="text-xs text-muted-foreground">Fallas</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.total_sugerencias || 0}</p>
                <p className="text-xs text-muted-foreground">Sugerencias</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.solucionados || 0}</p>
                <p className="text-xs text-muted-foreground">Resueltos</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.en_proceso || 0}</p>
                <p className="text-xs text-muted-foreground">En Proceso</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filtros:</span>
            </div>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="falla">Fallas</SelectItem>
                <SelectItem value="sugerencia">Sugerencias</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="enviado">Enviado</SelectItem>
                <SelectItem value="recibido">Recibido</SelectItem>
                <SelectItem value="en_proceso">En Proceso</SelectItem>
                <SelectItem value="solucionado">Solucionado</SelectItem>
                <SelectItem value="aceptado">Aceptado</SelectItem>
                <SelectItem value="rechazado">Rechazado</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant={myReports ? "default" : "outline"}
              size="sm"
              onClick={() => setMyReports(!myReports)}
            >
              Mis Reportes
            </Button>
          </div>
        </div>

        {/* Issues List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              Cargando...
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No hay reportes</p>
              <Button 
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                Crear primer reporte
              </Button>
            </div>
          ) : (
            <AnimatePresence>
              {issues.map((issue, idx) => {
                const StatusIcon = STATUS_CONFIG[issue.status]?.icon || AlertCircle;
                const isExpanded = expandedIssue === issue.id;
                
                return (
                  <motion.div
                    key={issue.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-card border border-border rounded-xl overflow-hidden"
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => setExpandedIssue(isExpanded ? null : issue.id)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Type Icon */}
                        <div className={`p-2 rounded-lg ${issue.type === 'falla' ? 'bg-red-500/20' : 'bg-yellow-500/20'}`}>
                          {issue.type === 'falla' ? (
                            <Bug className="w-5 h-5 text-red-400" />
                          ) : (
                            <Lightbulb className="w-5 h-5 text-yellow-400" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-foreground truncate">
                              {issue.title}
                            </h3>
                            <Badge variant="outline" className={STATUS_CONFIG[issue.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {STATUS_CONFIG[issue.status]?.label || issue.status}
                            </Badge>
                            <Badge variant="outline" className={PRIORITY_CONFIG[issue.priority]?.color}>
                              {PRIORITY_CONFIG[issue.priority]?.label || issue.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {issue.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>{issue.category_name}</span>
                            <span>•</span>
                            <span>{issue.created_by_name}</span>
                            <span>•</span>
                            <span>{formatDate(issue.created_at)}</span>
                          </div>
                        </div>
                        
                        {/* Expand Icon */}
                        <div className="text-muted-foreground">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-border"
                        >
                          <div className="p-4 space-y-4 bg-accent/30">
                            {issue.affected_module && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Módulo Afectado</p>
                                <p className="text-sm text-foreground font-mono bg-background/50 p-2 rounded">
                                  {issue.affected_module}
                                </p>
                              </div>
                            )}
                            
                            {issue.steps_to_reproduce && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Pasos para Reproducir</p>
                                <p className="text-sm text-foreground whitespace-pre-wrap">
                                  {issue.steps_to_reproduce}
                                </p>
                              </div>
                            )}
                            
                            {issue.expected_behavior && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Comportamiento Esperado</p>
                                <p className="text-sm text-foreground">
                                  {issue.expected_behavior}
                                </p>
                              </div>
                            )}
                            
                            {issue.admin_notes && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Notas del Admin</p>
                                <p className="text-sm text-foreground bg-primary/10 p-2 rounded">
                                  {issue.admin_notes}
                                </p>
                              </div>
                            )}
                            
                            {issue.resolution_notes && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Notas de Resolución</p>
                                <p className="text-sm text-foreground bg-green-500/10 p-2 rounded">
                                  {issue.resolution_notes}
                                </p>
                              </div>
                            )}
                            
                            {/* Actions */}
                            <div className="flex gap-2 pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetail(issue);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver Prompt
                              </Button>
                              
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(issue);
                                  }}
                                >
                                  <Pencil className="w-4 h-4 mr-1" />
                                  Editar Estado
                                </Button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <DialogHeader>
            <DialogTitle>Nuevo Reporte</DialogTitle>
            <DialogDescription>
              Reporta una falla o sugiere una mejora
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Type Selection */}
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'falla', category: '' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'falla' 
                    ? 'border-red-500 bg-red-500/10' 
                    : 'border-border hover:border-red-500/50'
                }`}
              >
                <Bug className={`w-8 h-8 mx-auto mb-2 ${formData.type === 'falla' ? 'text-red-500' : 'text-muted-foreground'}`} />
                <p className={`font-semibold ${formData.type === 'falla' ? 'text-red-500' : 'text-foreground'}`}>
                  Falla
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Algo no funciona correctamente
                </p>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'sugerencia', category: '' })}
                className={`p-4 rounded-xl border-2 transition-all ${
                  formData.type === 'sugerencia' 
                    ? 'border-yellow-500 bg-yellow-500/10' 
                    : 'border-border hover:border-yellow-500/50'
                }`}
              >
                <Lightbulb className={`w-8 h-8 mx-auto mb-2 ${formData.type === 'sugerencia' ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                <p className={`font-semibold ${formData.type === 'sugerencia' ? 'text-yellow-500' : 'text-foreground'}`}>
                  Sugerencia
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Idea de mejora o nueva función
                </p>
              </button>
            </div>
            
            {/* Title */}
            <div>
              <label className="text-sm font-medium text-foreground">Título *</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Describe brevemente el problema o sugerencia"
                className="mt-1"
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="text-sm font-medium text-foreground">Descripción *</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Explica con detalle..."
                rows={4}
                className="mt-1"
              />
            </div>
            
            {/* Category & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-foreground">Categoría *</label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories[formData.type] || []).map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-foreground">Prioridad</label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Optional Fields */}
            <div>
              <label className="text-sm font-medium text-foreground">Módulo Afectado (opcional)</label>
              <Input
                value={formData.affected_module}
                onChange={(e) => setFormData({ ...formData, affected_module: e.target.value })}
                placeholder="Ej: Pantalla de Replays, Botón flotante..."
                className="mt-1"
              />
            </div>
            
            {formData.type === 'falla' && (
              <div>
                <label className="text-sm font-medium text-foreground">Pasos para Reproducir (opcional)</label>
                <Textarea
                  value={formData.steps_to_reproduce}
                  onChange={(e) => setFormData({ ...formData, steps_to_reproduce: e.target.value })}
                  placeholder="1. Ir a...&#10;2. Hacer clic en...&#10;3. Ver error..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-foreground">Comportamiento Esperado (opcional)</label>
              <Textarea
                value={formData.expected_behavior}
                onChange={(e) => setFormData({ ...formData, expected_behavior: e.target.value })}
                placeholder="¿Qué debería pasar?"
                rows={2}
                className="mt-1"
              />
            </div>
            
            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreate}>
                <Send className="w-4 h-4 mr-2" />
                Enviar Reporte
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal (View Prompt) */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-3xl max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedIssue?.type === 'falla' ? (
                <Bug className="w-5 h-5 text-red-500" />
              ) : (
                <Lightbulb className="w-5 h-5 text-yellow-500" />
              )}
              {selectedIssue?.title}
            </DialogTitle>
            <DialogDescription>
              ID: {selectedIssue?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="mt-4 space-y-4">
              <div className="flex gap-2">
                <Badge variant="outline" className={STATUS_CONFIG[selectedIssue.status]?.color}>
                  {STATUS_CONFIG[selectedIssue.status]?.label}
                </Badge>
                <Badge variant="outline" className={PRIORITY_CONFIG[selectedIssue.priority]?.color}>
                  {PRIORITY_CONFIG[selectedIssue.priority]?.label}
                </Badge>
                <Badge variant="outline">{selectedIssue.category_name}</Badge>
              </div>
              
              <div className="bg-accent/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">Prompt Generado</p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyPrompt(selectedIssue)}
                  >
                    {copied ? (
                      <Check className="w-4 h-4 mr-1 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 mr-1" />
                    )}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </Button>
                </div>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-background/50 p-4 rounded-lg max-h-[400px] overflow-y-auto">
                  {selectedIssue.system_prompt}
                </pre>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Copia este prompt y pégalo en tu agente de IA para resolver la falla o analizar la sugerencia
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal (Admin Only) */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Estado</DialogTitle>
            <DialogDescription>
              {selectedIssue?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-foreground">Estado</label>
              <Select 
                value={editData.status} 
                onValueChange={(v) => setEditData({ ...editData, status: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enviado">Enviado</SelectItem>
                  <SelectItem value="recibido">Recibido</SelectItem>
                  <SelectItem value="en_proceso">En Proceso</SelectItem>
                  {selectedIssue?.type === 'falla' ? (
                    <>
                      <SelectItem value="solucionado">Solucionado</SelectItem>
                      <SelectItem value="sin_solucion">Sin Solución</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="aceptado">Aceptado</SelectItem>
                      <SelectItem value="rechazado">Rechazado</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground">Notas del Admin</label>
              <Textarea
                value={editData.admin_notes}
                onChange={(e) => setEditData({ ...editData, admin_notes: e.target.value })}
                placeholder="Notas internas..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground">Notas de Resolución</label>
              <Textarea
                value={editData.resolution_notes}
                onChange={(e) => setEditData({ ...editData, resolution_notes: e.target.value })}
                placeholder="¿Cómo se resolvió o por qué se rechazó?"
                rows={3}
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdate}>
                Guardar Cambios
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
